import React, { useMemo } from 'react';
import { getPremiumEssentialsSafe } from '@wix/premium-fed-sdk/@premium-essentials';
import { ExperimentSpecs } from '../constants';
import { PerceptionCardNote } from './PerceptionCardNote';
import { PERCEPTION_CONTENT } from './perceptionContent';
import { priceContentFor } from './PerceptionPriceContent';
import { perceptionRowFor, withVoucherClaim } from './perceptionRows';
import { useVoucherEligibleTld } from './useVoucherEligibleTld';
import { parseDomain } from '@wix/domains-serverless-interface';
export const usePerceptionEnabled = () => {
  var _getPremiumEssentials;
  return Boolean((_getPremiumEssentials = getPremiumEssentialsSafe()) == null || (_getPremiumEssentials = _getPremiumEssentials.experiments) == null ? void 0 : _getPremiumEssentials[ExperimentSpecs.PricingPerceptionFirstYear]);
};
const rowFor = (content, isVoucherEligible, price, domain) => withVoucherClaim(perceptionRowFor(price, content), isVoucherEligible(domain ? parseDomain(domain).tld : undefined));
const priceNoteFor = (content, isVoucherEligible) => {
  const PriceNote = _ref => {
    let {
      price,
      domain,
      bulletAligned,
      size
    } = _ref;
    const row = rowFor(content, isVoucherEligible, price, domain);
    return row ? /*#__PURE__*/React.createElement(PerceptionCardNote, {
      row: row,
      domain: domain,
      bulletAligned: bulletAligned,
      size: size
    }) : null;
  };
  return PriceNote;
};
const priceNoteAppliesFor = (content, isVoucherEligible) => (price, domain) => {
  const row = rowFor(content, isVoucherEligible, price, domain);
  return Boolean((row == null ? void 0 : row.cardNote) || (row == null ? void 0 : row.claimPending));
};
const domainNoteFor = function (content, isVoucherEligible, _temp) {
  let {
    exactMatchOnly = false,
    bulletAligned = true,
    size = 'medium'
  } = _temp === void 0 ? {} : _temp;
  const DomainNote = _ref2 => {
    let {
      domain,
      price,
      exactMatch
    } = _ref2;
    const row = rowFor(content, isVoucherEligible, price, domain);
    if (exactMatchOnly && !exactMatch) {
      return null;
    }
    return row && domain ? /*#__PURE__*/React.createElement(PerceptionCardNote, {
      row: row,
      domain: domain,
      bulletAligned: bulletAligned,
      size: size
    }) : null;
  };
  return DomainNote;
};
/**
 * The results rows carry the claim under the price and leave the figure unemphasised; the hero
 * cards show it beside the domain, so their price carries the emphasis instead.
 */
const anonymousResultsConfig = (content, isVoucherEligible) => ({
  Layouts: {
    PricingContent: priceContentFor(content, {
      noteBelowPrice: true,
      emphasizePrice: false
    })
  },
  heroLayouts: {
    PricingContent: priceContentFor(content, {
      noteBelowPrice: false,
      emphasizePrice: true
    })
  },
  PriceNote: priceNoteFor(content, isVoucherEligible),
  priceNoteApplies: priceNoteAppliesFor(content, isVoucherEligible)
});

/**
 * The gate cards state the claim under the domain, so the price column shows only the figures and
 * the label — and the figure stays in the standard colour, unlike the anonymous hero.
 */
const businessManagerGateConfig = (content, isVoucherEligible) => ({
  Layouts: {
    PricingContent: priceContentFor(content, {
      noteBelowPrice: false,
      emphasizePrice: false
    }),
    DomainNote: domainNoteFor(content, isVoucherEligible, {
      bulletAligned: true,
      size: 'small'
    })
  }
});

/**
 * The BM results page places the claim differently per surface, so it cannot reuse the gate's
 * config: its list rows carry the claim as a badge under the price, while the compact-hero and main
 * cards the BM optimizations release turns on state it beside the domain. Those cards are siblings
 * of the list, so they take it via `PriceNote` rather than the `DomainNote` slot; without
 * that, they would sit above rows carrying the arm while showing baseline pricing themselves.
 *
 * Same placement as the anonymous flow, then, but the figure is never recoloured on this surface —
 * green is reserved for the note, badge and banner.
 *
 * The exact-match row is the one exception: it states the claim under its domain, where the TLD it
 * names is the domain the sentence is about. `PricingContent` withholds the badge on that row so the
 * two never say the same thing twice.
 */
const businessManagerResultsConfig = (content, isVoucherEligible) => ({
  Layouts: {
    PricingContent: priceContentFor(content, {
      noteBelowPrice: true,
      emphasizePrice: false
    }),
    DomainNote: domainNoteFor(content, isVoucherEligible, {
      exactMatchOnly: true,
      bulletAligned: false
    })
  },
  heroLayouts: {
    PricingContent: priceContentFor(content, {
      noteBelowPrice: false,
      emphasizePrice: false
    })
  },
  PriceNote: priceNoteFor(content, isVoucherEligible),
  priceNoteApplies: priceNoteAppliesFor(content, isVoucherEligible)
});

/**
 * A surface is a placement, not an audience: the two Business Manager surfaces share every string
 * and disagree only on where the claim sits.
 */

const SURFACES = {
  anonymousResults: {
    audience: 'anonymous',
    config: anonymousResultsConfig
  },
  businessManagerGate: {
    audience: 'businessManager',
    config: businessManagerGateConfig
  },
  businessManagerResults: {
    audience: 'businessManager',
    config: businessManagerResultsConfig
  }
};
export const usePerceptionPricing = function (surface) {
  if (surface === void 0) {
    surface = 'anonymousResults';
  }
  const enabled = usePerceptionEnabled();
  const isVoucherEligible = useVoucherEligibleTld(enabled);
  return useMemo(() => {
    if (!enabled) {
      return {};
    }
    const {
      audience,
      config
    } = SURFACES[surface];
    return config(PERCEPTION_CONTENT[audience], isVoucherEligible);
  }, [enabled, surface, isVoucherEligible]);
};
//# sourceMappingURL=usePerceptionPricing.js.map